import { type ColumnDef } from '@tanstack/react-table';

import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import type { OpsOrder } from '@/graphql/generatedTypes';
import i18n, { getDateFormatOptions } from '@/utils/i18n';

import { OrderColumnHeader } from './ColumnHeader';
import { OrderStatusBadge } from './OrderStatusBadge';

export function selectColumn(): ColumnDef<OpsOrder> {
  return {
    id: 'select',
    meta: {
      disableRowNavigation: true,
    },
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };
}

export function statusColumn(): ColumnDef<OpsOrder> {
  return {
    accessorKey: 'status',
    meta: {
      get title() {
        return i18n.t('Order:columnStatus');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Order:columnStatus')} />
    ),
    cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
  };
}

export function dateColumn({
  id,
  titleKey,
  accessorKey,
  showTime,
}: {
  id: string;
  titleKey: string;
  accessorKey: string;
  showTime?: boolean;
}): ColumnDef<OpsOrder> {
  return {
    id,
    accessorKey,
    meta: {
      get title() {
        return i18n.t(titleKey);
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t(titleKey)} />
    ),
    cell: ({ row }) => {
      const ts = (row.original as Record<string, unknown>)[accessorKey];
      if (!ts) return '-';
      const date = new Date(ts as string);
      if (showTime) {
        const dateStr = date.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        const timeStr = date.toLocaleTimeString(i18n.language, {
          hour: '2-digit',
          minute: '2-digit',
        });
        return <span>{`${dateStr} ${timeStr}`}</span>;
      }
      return new Date(ts as string).toLocaleDateString(
        i18n.language,
        getDateFormatOptions(!!showTime),
      );
    },
  };
}

export function amountColumn(): ColumnDef<OpsOrder> {
  return {
    id: 'amount',
    meta: {
      get title() {
        return i18n.t('Order:columnAmount');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Order:columnAmount')} />
    ),
    accessorFn: (row) => row.total_amount ?? 0,
    cell: ({ row }) => (
      <Badge variant="outline" className="tw:justify-center tw:tabular-nums">
        {row.original.total_amount}
      </Badge>
    ),
    enableSorting: false,
  };
}

export function warehouseColumn(
  titleKey = 'Order:columnWarehouse',
): ColumnDef<OpsOrder> {
  return {
    id: 'warehouse',
    meta: {
      get title() {
        return i18n.t(titleKey);
      },
    },
    header: () => i18n.t(titleKey),
    accessorFn: (row) => row.warehouse?.name ?? '-',
    cell: ({ row }) => row.original.warehouse?.name || '-',
  };
}

export function actionsColumn(
  Component: React.ComponentType<{
    row: import('@tanstack/react-table').Row<OpsOrder>;
  }>,
): ColumnDef<OpsOrder> {
  return {
    id: 'actions',
    meta: {
      disableRowNavigation: true,
    },
    cell: ({ row }) => <Component row={row} />,
    enableSorting: false,
    enableHiding: false,
  };
}
